import dayjs from "dayjs";
import db from "@/db";
import { useUsersStore } from "@/store/modules/users";
import { logRequestFailure } from "@/utils/network";
import { reportDeviceDataError } from "@/utils/deviceDataError";
import { wait } from "@/utils/helpers";

const ONLINE_START_DEADLINE_MS = 10 * 1000;
const PAST_DEADLINE = Symbol("pastDeadline");

const initOfflineMode = async () => {
  const user = await useUsersStore().getCachedCurrentUser();
  const weeksValidity =
    Number.parseInt(import.meta.env.VITE_APP_OFFLINE_WEEKS_VALIDITY) || 6;
  const endValidityDate = dayjs().subtract(weeksValidity, "week");
  const lastOnlineAuth = dayjs(user?.lastOnlineAuthAt ?? null);
  if (
    user &&
    lastOnlineAuth.isValid() &&
    lastOnlineAuth.isAfter(endValidityDate) &&
    (await user.hasOfflinePermission())
  ) {
    await db.models.User.createOrUpdate(user);
  } else {
    throw new Error("Unauthorized access to offline mode");
  }
};

const reachApi = async (usersStore) => {
  await usersStore.initializeCurrentUser();
  await db.models.User.createOrUpdate(usersStore.currentUser);
};

const startOffline = async (usersStore) => {
  await initOfflineMode();
  await usersStore.restoreCachedCurrentUser().catch((error) => {
    console.error("Could not restore cached user", error);
    reportDeviceDataError(error, { risk: "cached_user_unavailable" });
  });
};

const reachApiOrStartOffline = async (usersStore) => {
  const online = reachApi(usersStore);
  online.catch(() => {});
  const first = await Promise.race([
    online,
    wait(ONLINE_START_DEADLINE_MS).then(() => PAST_DEADLINE),
  ]);
  if (first !== PAST_DEADLINE) return;
  try {
    await startOffline(usersStore);
  } catch {
    await online;
    return;
  }
  online
    .then(() => db.models.User.createOrUpdate(usersStore.currentUser))
    .catch(() => {});
};

const isLoggedOrOffline = async (to, from, next) => {
  const usersStore = useUsersStore();

  if (window.navigator.onLine) {
    try {
      await reachApiOrStartOffline(usersStore);
      return next();
    } catch (error) {
      logRequestFailure("Could not reach the API, falling back offline", error);
    }
  }

  try {
    await startOffline(usersStore);
  } catch {
    return next({ name: "unauthorized-access-offline-mode" });
  }
  next();
};

const isOffline = (from, to, next) => {
  if (window.navigator.onLine) {
    next("/");
  } else {
    next();
  }
};

export { isLoggedOrOffline, isOffline };
